import { useEffect, useRef, useState } from "react";
import { watchLocation, type WatchHandle } from "@/utils/nativeGeolocation";

/**
 * Returns a live compass heading in degrees (0 = north, 90 = east),
 * or null if heading isn't available yet.
 *
 * Source priority:
 *   1. Device orientation (magnetometer) — instant, works while stationary.
 *      Requires a one-time permission prompt on iOS Safari.
 *   2. GPS course bearing — derived from successive geolocation fixes.
 *      Always available outdoors, but jittery / undefined when not moving.
 *
 * The hook only attaches listeners while `enabled` is true, so when the
 * compass toggle is off we incur zero battery cost.
 */
export function useHeading(enabled: boolean): {
  heading: number | null;
  /** Call from a user gesture to request iOS DeviceOrientation permission. */
  requestPermission: () => Promise<boolean>;
} {
  const [heading, setHeading] = useState<number | null>(null);
  const lastFixRef = useRef<{ lat: number; lng: number; t: number } | null>(null);
  // Throttle state — `deviceorientation` can fire 60Hz on iOS, which would
  // re-render the entire home page on every tick. We coalesce to ~10Hz and
  // only commit when the heading changed by ≥2° (visually imperceptible
  // updates below that just churn React for no reason).
  const lastEmitRef = useRef<{ t: number; h: number } | null>(null);

  useEffect(() => {
    if (!enabled) {
      setHeading(null);
      lastFixRef.current = null;
      lastEmitRef.current = null;
      return;
    }

    const commitHeading = (h: number) => {
      const now = performance.now();
      const last = lastEmitRef.current;
      if (last) {
        if (now - last.t < 100) return; // ~10Hz cap
        let delta = Math.abs(h - last.h);
        if (delta > 180) delta = 360 - delta;
        if (delta < 2) return; // imperceptible change
      }
      lastEmitRef.current = { t: now, h };
      setHeading(h);
    };

    // ---- Device orientation (magnetometer) ----
    const onOrient = (e: DeviceOrientationEvent) => {
      // iOS exposes a true-north-corrected `webkitCompassHeading`.
      // Other browsers give `alpha` (z-axis rotation, 0 at device's "north").
      // alpha increases counter-clockwise, so heading = 360 - alpha.
      const wk = (e as DeviceOrientationEvent & { webkitCompassHeading?: number })
        .webkitCompassHeading;
      let h: number | null = null;
      if (typeof wk === "number" && !Number.isNaN(wk)) {
        h = wk;
      } else if (typeof e.alpha === "number" && !Number.isNaN(e.alpha)) {
        h = (360 - e.alpha) % 360;
      }
      if (h != null) commitHeading(h);
    };

    // Prefer the absolute event when supported (true magnetic heading).
    const orientEvent = "ondeviceorientationabsolute" in window
      ? "deviceorientationabsolute"
      : "deviceorientation";
    window.addEventListener(orientEvent, onOrient as EventListener);

    // ---- GPS course fallback ----
    // Wrapped through nativeGeolocation so we get the real CLLocationManager
    // course on iOS / FusedLocationProviderClient on Android when running in
    // the native shell, and fall back to navigator.geolocation on the web.
    let watchHandle: WatchHandle | null = null;
    let cancelled = false;
    void watchLocation(
      (fix) => {
        if (cancelled) return;
        // Native course (only defined on real devices when moving).
        const native = fix.heading;
        if (typeof native === "number" && !Number.isNaN(native) && native >= 0) {
          // Only use GPS course when we don't have orientation, since
          // the magnetometer is more responsive.
          setHeading((cur) => (cur == null ? native : cur));
        }
        // Derive bearing from the last fix as a final fallback.
        const prev = lastFixRef.current;
        const cur = { lat: fix.lat, lng: fix.lng, t: fix.timestamp };
        if (prev && cur.t - prev.t < 30_000) {
          const b = bearing(prev.lat, prev.lng, cur.lat, cur.lng);
          const dist = haversine(prev.lat, prev.lng, cur.lat, cur.lng);
          // Only trust derived bearing if we actually moved (>3m).
          if (dist > 3) {
            setHeading((curH) => (curH == null ? b : curH));
          }
        }
        lastFixRef.current = cur;
      },
      () => { /* swallow — heading is a best-effort enhancement */ },
      { enableHighAccuracy: true, maximumAgeMs: 2000, timeoutMs: 10_000 },
    ).then((h) => {
      if (cancelled) { h.stop(); return; }
      watchHandle = h;
    });

    return () => {
      cancelled = true;
      window.removeEventListener(orientEvent, onOrient as EventListener);
      watchHandle?.stop();
    };
  }, [enabled]);

  const requestPermission = async (): Promise<boolean> => {
    // iOS 13+ requires explicit permission via a user gesture.
    const Anything = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<"granted" | "denied">;
    };
    if (typeof Anything.requestPermission === "function") {
      try {
        const r = await Anything.requestPermission();
        return r === "granted";
      } catch {
        return false;
      }
    }
    return true; // Other browsers don't gate the event.
  };

  return { heading, requestPermission };
}

function toRad(d: number) { return (d * Math.PI) / 180; }
function toDeg(r: number) { return (r * 180) / Math.PI; }

function bearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lng2 - lng1);
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
